import { useContext } from 'react'

import { type ChannelId } from '@onr/schema'
import { useNavigate } from '@tanstack/react-router'

import { type AppViewProps } from '@/components/templates/AppView'
import { useGoogleMapConfiguration } from '@/lib/useGoogleMapConfiguration.ts'
import { useNotificationSoundControl } from '@/model/contexts/useNotificationSoundControl.ts'
import { useRegion } from '@/model/contexts/useRegion.ts'

import { useAppActions } from './actions/useAppActions.ts'
import { useAppLabels } from './labels/useAppLabels.ts'
import { useTimeAgoFormatter, useWillExecuteInTimeAgoFormatter } from './labels/useTimeAgoFormatter.ts'
import { useAppNavigation } from './navigation/useAppNavigation.ts'
import { AppPageDataContext } from './AppPageDataProvider.tsx'
import { AppPageMapContext } from './AppPageMapProvider.tsx'
import { AppPageStateContext } from './AppPageStateProvider.tsx'

export const useDashboardPage = (): AppViewProps => {
  const { region } = useRegion()
  const labels = useAppLabels()
  const actions = useAppActions()
  const map = useContext(AppPageMapContext)
  const state = useContext(AppPageStateContext)
  const data = useContext(AppPageDataContext)
  const navigation = useAppNavigation()
  const timeAgoFormatter = useTimeAgoFormatter()
  const willExecuteInTimeAgoFormatter = useWillExecuteInTimeAgoFormatter()
  const googleMapConfiguration = useGoogleMapConfiguration()
  const navigate = useNavigate()
  const { toggleSound } = useNotificationSoundControl()

  const onTeamMessageClick = (teamId: string) =>
    navigate({ to: '/dashboard/$regionId/messages/$teamId', params: { regionId: region._id, teamId } })

  const onChannelMessageClick = (channelId: ChannelId) =>
    navigate({ to: '/dashboard/$regionId/messages/channels/$channelId', params: { regionId: region._id, channelId } })

  return {
    ...labels,
    ...actions,
    ...map,
    ...state,
    ...data,
    ...navigation,
    timeAgoFormatter,
    willExecuteInTimeAgoFormatter,
    googleMapConfiguration,
    onTeamMessageClick,
    onChannelMessageClick,
    onNotificationSoundToggled: toggleSound,
  }
}
